import React, {useRef, useState} from "react";
import {Button, Column, Row, Text} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {observer_mgl} from "mobx-graphlink";
import {GetUpdates} from "web-vcore";
import {DialogStyle} from "../../../Utils/UI/GlobalStyles.js";
import {MapObject} from "../../../Server/DB/mapObjects/@MapObject.js";
import {GetMapObject} from "../../../Server/DB/mapObjects.js";
import {UpdateMapObject} from "../../../Server/Commands/MapObjectCommands.js";
import {DMap} from "../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../Store/main/maps/@MapState.js";

/** The shape of a map-object export file (written by the "Export all map-objects" button in
 *  SettingsUI): a wrapper object carrying the map + a flat `mapObjects` array. */
type MapObjectsExportFile = {
	map: any;
	mapObjects: MapObject[];
};

export function ShowImportFromFileDialog(args: {map: DMap, mapState: MapState}) {
	ShowMessageBox({
		title: "Import from file",
		cancelButton: true,
		cancelButtonProps: {text: "Close"},
		okButton: false,
		
		// don't use overlay/background-blocker
		overlayStyle: {background: "none", pointerEvents: "none"},
		containerStyle: {pointerEvents: "auto"},

		message: ()=><ImportFromFileDialogUI map={args.map} mapState={args.mapState}/>,
	});
}

const ImportFromFileDialogUI = observer_mgl((props: {map: DMap, mapState: MapState})=>{
	const {map, mapState} = props;
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Objects found in the selected export file, keyed by id (the "from-export" data).
	const [importedObjects, setImportedObjects] = useState<Map<string, MapObject>>(new Map());
	// Ids of objects the user has chosen to restore (subset of importedObjects).
	const [restoreIDs, setRestoreIDs] = useState<string[]>([]);
	// Per-object restore feedback (e.g. "Restored" after a successful restore command).
	const [restoredIDs, setRestoredIDs] = useState<Set<string>>(new Set());

	const activeObject = GetMapObject(mapState.activeObject, map.id);
	const selectedObjects = mapState.selectedObjects.map(id=>GetMapObject(id, map.id)).filter(a=>a != null) as MapObject[];

	const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>)=>{
		const file = e.target.files?.[0];
		e.target.value = ""; // allow re-selecting the same file later
		if (!file) return;
		const reader = new FileReader();
		reader.onload = ()=>{
			try {
				const parsed = JSON.parse(reader.result as string) as MapObjectsExportFile;
				const newImported = new Map<string, MapObject>();
				for (const obj of parsed.mapObjects ?? []) {
					newImported.set(obj.id, obj);
				}
				setImportedObjects(newImported);
				// Keep only restore-ids that still exist in the new import.
				setRestoreIDs(prev=>prev.filter(id=>newImported.has(id)));
				setRestoredIDs(new Set());
			} catch {
				setImportedObjects(new Map());
			}
		};
		reader.readAsText(file);
	};

	const AddIDs = (ids: (string|n)[])=>{
		setRestoreIDs(prev=>{
			const next = new Set(prev);
			for (const id of ids) {
				if (id != null && importedObjects.has(id)) next.add(id);
			}
			return Array.from(next);
		});
	};

	const Restore = (id: string)=>{
		const fromExport = importedObjects.get(id);
		const current = GetMapObject(id, map.id);
		if (!fromExport || !current) return;
		const updates = GetUpdates(current, fromExport);
		new UpdateMapObject({id, updates}).RunOnServer();
		setRestoredIDs(prev=>{
			const next = new Set(prev);
			next.add(id);
			return next;
		});
	};

	// Number of fields that differ between the current live object and its from-export data.
	const CountDifferingFields = (id: string)=>{
		const fromExport = importedObjects.get(id);
		const current = GetMapObject(id, map.id);
		if (!fromExport || !current) return 0;
		return Object.keys(GetUpdates(current, fromExport)).length;
	};
	const GetDisplayName = (id: string)=>{
		const current = GetMapObject(id, map.id);
		const fromExport = importedObjects.get(id);
		return (current?.name || fromExport?.name) ?? "(unnamed)";
	};

	return (
		<Column style={DialogStyle({width: 620})}>
			<Row>
				<Text style={{opacity: 0.7}}>Select a JSON file from an earlier export, then add objects to the restore list.</Text>
			</Row>
			<Row mt={5}>
				<Button text="Select export file..." onClick={()=>fileInputRef.current?.click()}/>
				<input ref={fileInputRef} type="file" accept="application/json,.json"
					style={{display: "none"}} onChange={onFileSelected}/>
				<Text ml={10} style={{opacity: 0.7, fontSize: 12}}>
					{importedObjects.size > 0 ? `${importedObjects.size} object(s) loaded from file.` : "No file selected."}
				</Text>
			</Row>

			<Row mt={10} style={{alignItems: "center"}}>
				<Text style={{fontWeight: "bold"}}>Objects to restore:</Text>
				<Button ml="auto" text="Add active"
					enabled={activeObject != null && importedObjects.has(activeObject.id)}
					title={activeObject == null ? "No active object." : importedObjects.has(activeObject.id) ? `Add "${activeObject.name}" to the restore list.` : "Active object not present in the export file."}
					onClick={()=>AddIDs([activeObject?.id])}/>
				<Button ml={5} text="Add selected"
					enabled={selectedObjects.some(o=>importedObjects.has(o.id))}
					title={selectedObjects.length == 0 ? "No selected objects." : "Add all selected objects present in the export file."}
					onClick={()=>AddIDs(selectedObjects.map(o=>o.id))}/>
			</Row>

			<Column mt={5} style={{
				maxHeight: 320,
				overflowY: "auto",
				background: "rgba(0,0,0,0.2)",
				borderRadius: 4,
				padding: "4px 8px",
			}}>
				{restoreIDs.length == 0 && (
					<Text style={{opacity: 0.6, fontStyle: "italic"}}>No objects added yet. Load a file, then use "Add active" / "Add selected".</Text>
				)}
				{restoreIDs.map(id=>{
					const diffCount = CountDifferingFields(id);
					const restored = restoredIDs.has(id);
					return (
						<Row key={id} mt={3} style={{alignItems: "center"}}>
							<Button mdIcon="delete" title="Remove from restore list" onClick={()=>{
								setRestoreIDs(prev=>prev.filter(a=>a != id));
								setRestoredIDs(prev=>{
									const next = new Set(prev);
									next.delete(id);
									return next;
								});
							}}/>
							<Text title={id} style={{minWidth: 130, fontFamily: "monospace", opacity: 0.6, fontSize: 12}}>
								{id.slice(0, 8)}…
							</Text>
							<Text style={{flex: 1, fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
								{GetDisplayName(id)}
							</Text>
							<Text style={{opacity: 0.8, fontSize: 12}}>{diffCount} field{diffCount == 1 ? "" : "s"} differ</Text>
							<Button ml={8} text={restored ? "Restored" : "Restore"}
								enabled={diffCount > 0 && !restored}
								style={restored ? {color: "limegreen"} : undefined}
								onClick={()=>Restore(id)}/>
						</Row>
					);
				})}
			</Column>
		</Column>
	);
});
